import React, { useMemo, useState } from 'react';
import { Button, Grid, MenuItem, Select } from '@mui/material';
import Typography from '@mui/material/Typography';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { AccountData, OpenBalancesTab, OrderData, OrderService } from '../api';
import { useAuth } from '../session/InternalAuthProvider';
import { useCalendar } from '../calendar/CalendarContext/CalendarContext';
import { useUsers } from '../session/swr/useUsers';
import { getUserFullNameFromUserId } from '../calendar/components/UsersMultiSelect';
import EditOrderDialog from '../calendar/dialogs/EditOrderDialog/EditOrderDialog';
import { CloseBillWithNoteDialog } from '../calendar/agendaView/components/CloseBillWithNoteDialog';
import { DATE_FORMAT } from '../calendar/consts';
import { getDateFormat, getTimeFormat } from '../common/getDateAndTimeFormat';
import { CellProps, useTable } from '../table/useTable';
import { TABLE_PHONE_ID } from '../table/TableComponent';
import TimeUtils from '../utils/TimeUtils';
import { BillAgeTag } from './BillAgeTag';
import {
  formatBillAmount,
  formatBillDateTime,
  getItemsTotal,
  getOrderCalendarPath,
  getOrderName,
} from './openBillUtils';

enum OpenBillsDateRange {
  ALL = 'all',
  LAST_7_DAYS = 'last_7_days',
  LAST_30_DAYS = 'last_30_days',
}

const DAYS_BY_DATE_RANGE: Record<OpenBillsDateRange, number | undefined> = {
  [OpenBillsDateRange.ALL]: undefined,
  [OpenBillsDateRange.LAST_7_DAYS]: 7,
  [OpenBillsDateRange.LAST_30_DAYS]: 30,
};

const DATE_RANGE_LABEL_KEYS: Record<OpenBillsDateRange, string> = {
  [OpenBillsDateRange.ALL]: 'open_bills.all_dates',
  [OpenBillsDateRange.LAST_7_DAYS]: 'open_bills.last_7_days',
  [OpenBillsDateRange.LAST_30_DAYS]: 'open_bills.last_30_days',
};

type OpenBillsFilterState = {
  dateFrom?: string;
};

type OpenBillsTableProps = {
  tab: OpenBalancesTab;
  accountToday: string;
  onBillsChanged: () => void;
};

export const OpenBillsTable = ({ tab, accountToday, onBillsChanged }: OpenBillsTableProps) => {
  const { t } = useTranslation();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const { shortDateFormat } = getDateFormat(account);
  const timeFormat = getTimeFormat(account);
  const { editOrderDrawerState, closeEditOrderDrawer, openEditOrderDrawer } = useCalendar();
  const { users } = useUsers();
  const [dateRange, setDateRange] = useState(OpenBillsDateRange.ALL);
  const [orderToClose, setOrderToClose] = useState<OrderData | null>(null);
  const isOpenTab = tab === OpenBalancesTab.OPEN;

  const rangeDays = DAYS_BY_DATE_RANGE[dateRange];
  const filterState = useMemo<OpenBillsFilterState>(
    () => ({
      dateFrom: rangeDays
        ? moment(accountToday, DATE_FORMAT).subtract(rangeDays, 'days').format(DATE_FORMAT)
        : undefined,
    }),
    [rangeDays, accountToday],
  );

  const fetcher = async ({
    search,
    page,
    perPage,
    filterState: filters,
  }: {
    search?: string;
    page: number;
    perPage: number;
    filterState?: OpenBillsFilterState;
  }) => (await OrderService.getOpenBalances(tab, page, perPage, search || undefined, filters?.dateFrom)).items;

  const collect = (order: OrderData) => openEditOrderDrawer({ order, focusExtraItems: true, openCheckout: true });

  const collectButton = (order: OrderData) => (
    <Button variant="contained" size="small" onClick={() => collect(order)} sx={{ minHeight: 32, boxShadow: 'none' }}>
      {t('calendar.areas_view.collect')}
    </Button>
  );

  const sharedCells: CellProps<OrderData>[] = [
    {
      id: 'date',
      alignment: 'center',
      label: t('open_bills.column_date'),
      render: (order: OrderData) => (
        <Grid container direction="column" alignItems="flex-start" gap={0.25}>
          <Typography variant="body2">{formatBillDateTime(order, shortDateFormat, timeFormat)}</Typography>
          {isOpenTab && order.date && <BillAgeTag date={order.date} accountToday={accountToday} />}
        </Grid>
      ),
    },
    {
      id: 'customer',
      alignment: 'center',
      label: t('open_bills.column_customer'),
      render: (order: OrderData) => getOrderName(order) || t('calendar.areas_view.unnamed_order'),
    },
    {
      id: 'order_number',
      alignment: 'center',
      label: t('open_bills.column_order'),
      render: (order: OrderData) => <Link to={getOrderCalendarPath(order)}>{`#${order.order_number}`}</Link>,
    },
  ];

  const openCells: CellProps<OrderData>[] = [
    {
      id: TABLE_PHONE_ID,
      alignment: 'center',
      label: t('open_bills.column_phone'),
    },
    {
      id: 'items',
      alignment: 'center',
      label: t('open_bills.column_items'),
      render: (order: OrderData) => formatBillAmount(getItemsTotal(order), account),
    },
    {
      id: 'paid',
      alignment: 'center',
      label: t('open_bills.column_paid'),
      render: (order: OrderData) => formatBillAmount(getItemsTotal(order) - (order.balance_due ?? 0), account),
    },
    {
      id: 'open',
      alignment: 'center',
      label: t('open_bills.column_open'),
      render: (order: OrderData) => (
        <Typography variant="body2" fontWeight={700} color="error.main">
          {formatBillAmount(order.balance_due ?? 0, account)}
        </Typography>
      ),
    },
    {
      id: 'actions',
      alignment: 'center',
      render: (order: OrderData) => (
        <Grid container justifyContent="flex-end" flexWrap="nowrap" gap={1}>
          <Button
            variant="outlined"
            color="inherit"
            size="small"
            onClick={() => setOrderToClose(order)}
            sx={{ minHeight: 32 }}>
            {t('calendar.areas_view.close_with_note')}
          </Button>
          {collectButton(order)}
        </Grid>
      ),
    },
  ];

  const closedCells: CellProps<OrderData>[] = [
    {
      id: 'not_paid',
      alignment: 'center',
      label: t('open_bills.column_not_paid'),
      render: (order: OrderData) => (
        <Typography variant="body2" fontWeight={700} color="error.main">
          {formatBillAmount(Math.max(0, order.balance_due ?? 0), account)}
        </Typography>
      ),
    },
    {
      id: 'note',
      alignment: 'center',
      width: 280,
      label: t('open_bills.column_note'),
      render: (order: OrderData) => (
        <Typography variant="body2" sx={{ whiteSpace: 'normal', textTransform: 'none' }}>
          {order.balance_closed_note}
        </Typography>
      ),
    },
    {
      id: 'closed_by',
      alignment: 'center',
      label: t('open_bills.column_closed_by'),
      render: (order: OrderData) => (
        <Typography variant="body2" color="text.secondary">
          {getUserFullNameFromUserId(users, order.balance_closed_by ?? '')}
          {order.balance_closed_at &&
            ` · ${TimeUtils.getLocalTime(order.balance_closed_at, account.timezone).format(
              `${shortDateFormat} ${timeFormat}`,
            )}`}
        </Typography>
      ),
    },
    {
      id: 'actions',
      alignment: 'center',
      render: (order: OrderData) => collectButton(order),
    },
  ];

  const { TableComponent, mutate } = useTable<OrderData>({
    opId: `getOpenBalances_${tab}`,
    fetcher,
    tableDetails: [...sharedCells, ...(isOpenTab ? openCells : closedCells)],
    filterState,
    scrollWithPage: true,
    actionsComponent: (
      <Select
        size="small"
        value={dateRange}
        onChange={(event) => setDateRange(event.target.value as OpenBillsDateRange)}
        sx={{ minWidth: 160 }}>
        {Object.values(OpenBillsDateRange).map((range) => (
          <MenuItem key={range} value={range}>
            {t(DATE_RANGE_LABEL_KEYS[range])}
          </MenuItem>
        ))}
      </Select>
    ),
  });

  const refreshBills = () => {
    void mutate();
    onBillsChanged();
  };

  return (
    <Grid container flexDirection="column" flexWrap="nowrap">
      {TableComponent}
      <EditOrderDialog
        editOrderDialogState={editOrderDrawerState}
        closeEditOrderDialog={() => {
          refreshBills();
          void closeEditOrderDrawer();
        }}
      />
      {orderToClose && (
        <CloseBillWithNoteDialog
          order={orderToClose}
          timeFormat={timeFormat}
          formatAmount={(amount) => formatBillAmount(amount, account)}
          onClose={() => setOrderToClose(null)}
          onClosed={() => {
            setOrderToClose(null);
            refreshBills();
          }}
          onStale={() => {
            setOrderToClose(null);
            refreshBills();
          }}
        />
      )}
    </Grid>
  );
};
